import api from './api';
import { 
  Call, 
  CallFilters, 
  CallStatistics, 
  CreateCallRequest, 
  UpdateCallRequest,
  RescheduleCallRequest,
  CancelCallRequest,
  EndCallRequest,
  LeadForCall
} from '../types/Call';

export const callsAPI = {
  // Get all calls with filters and pagination
  getCalls: async (filters: CallFilters = {}): Promise<{ data: Call[]; pagination: any }> => {
    const params = new URLSearchParams();
    
    if (filters.status) params.append('status', filters.status);
    if (filters.callType) params.append('callType', filters.callType);
    if (filters.leadId) params.append('leadId', filters.leadId.toString());
    if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.append('dateTo', filters.dateTo);
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

    const response = await api.get(`/calls?${params.toString()}`);
    return response.data;
  },

  // Get call by ID
  getCallById: async (id: number): Promise<Call> => {
    const response = await api.get(`/calls/${id}`);
    return response.data.data;
  },

  // Create new call
  createCall: async (callData: CreateCallRequest): Promise<Call> => {
    const response = await api.post('/calls', callData);
    return response.data.data;
  },

  // Update call
  updateCall: async (id: number, updateData: UpdateCallRequest): Promise<Call> => {
    const response = await api.put(`/calls/${id}`, updateData);
    return response.data.data;
  },

  // Start live call
  startCall: async (id: number): Promise<Call> => {
    const response = await api.post(`/calls/${id}/start`);
    return response.data.data;
  },

  // End live call
  endCall: async (id: number, endData: EndCallRequest): Promise<Call> => {
    const response = await api.post(`/calls/${id}/end`, endData);
    return response.data.data;
  },

  // Reschedule call
  rescheduleCall: async (id: number, rescheduleData: RescheduleCallRequest): Promise<Call> => {
    const response = await api.post(`/calls/${id}/reschedule`, rescheduleData);
    return response.data.data;
  },

  // Cancel call
  cancelCall: async (id: number, cancelData: CancelCallRequest): Promise<Call> => {
    const response = await api.post(`/calls/${id}/cancel`, cancelData);
    return response.data.data;
  },

  // Handle call not picked up
  handleNotPickedUp: async (id: number, data: { reason: string; rescheduleDate?: string }): Promise<{ cancelledCall: Call; newScheduledCall?: Call }> => {
    const response = await api.post(`/calls/${id}/not-picked-up`, data);
    return response.data.data;
  },

  // Get call statistics
  getCallStatistics: async (dateFrom?: string, dateTo?: string): Promise<CallStatistics> => {
    const params = new URLSearchParams();
    if (dateFrom) params.append('dateFrom', dateFrom);
    if (dateTo) params.append('dateTo', dateTo);

    const response = await api.get(`/calls/stats/overview?${params.toString()}`);
    return response.data.data;
  },

  // Get available leads for call creation
  getAvailableLeads: async (): Promise<LeadForCall[]> => {
    const response = await api.get('/calls/leads/available');
    return response.data.data;
  },

  // Delete call
  deleteCall: async (id: number): Promise<void> => {
    const response = await api.delete(`/calls/${id}`);
    return response.data;
  },

  // Reschedule cancelled call
  rescheduleCancelledCall: async (id: number, newDate: string, reason: string): Promise<Call> => {
    const response = await api.post(`/calls/${id}/reschedule-cancelled`, {
      newDate,
      reason
    });
    return response.data.data;
  }
};

// Utility functions
export const getCallStatusColor = (status: string): string => {
  switch (status) {
    case 'scheduled':
      return 'bg-blue-100 text-blue-800';
    case 'in_progress':
      return 'bg-yellow-100 text-yellow-800';
    case 'completed':
      return 'bg-green-100 text-green-800';
    case 'cancelled':
      return 'bg-red-100 text-red-800';
    case 'rescheduled':
      return 'bg-orange-100 text-orange-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export const getCallStatusLabel = (status: string): string => {
  switch (status) {
    case 'scheduled':
      return 'Scheduled';
    case 'in_progress':
      return 'In Progress';
    case 'completed':
      return 'Completed';
    case 'cancelled':
      return 'Cancelled';
    case 'rescheduled':
      return 'Rescheduled';
    default:
      return status;
  }
};

export const getCallTypeColor = (callType: string): string => {
  switch (callType) {
    case 'live':
      return 'bg-green-100 text-green-800';
    case 'recorded':
      return 'bg-blue-100 text-blue-800';
    case 'scheduled':
      return 'bg-purple-100 text-purple-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export const getCallTypeLabel = (callType: string): string => {
  switch (callType) {
    case 'live':
      return 'Live Call';
    case 'recorded':
      return 'Recorded Call';
    case 'scheduled':
      return 'Scheduled Call';
    default:
      return callType;
  }
};

export const formatCallDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${secs}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  } else {
    return `${secs}s`;
  }
};

export const formatCallDate = (dateString: string | null): string => {
  if (!dateString) return '-';
  
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};
